import React from 'react';
import { Alert, Button, Card } from 'react-bootstrap';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error details in state
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log to an error reporting service here
    // e.g., logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset error state and try again
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI when an error occurs
      return (
        <div className="error-boundary-fallback" style={{ padding: '2rem' }}>
          <Card className="border-danger">
            <Card.Header className="bg-danger text-white">
              <h4 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Something went wrong
              </h4>
            </Card.Header>
            <Card.Body>
              <Alert variant="danger">
                <p className="mb-0">
                  An unexpected error occurred in the Workout Tracker. This has been logged and you can try the following:
                </p>
              </Alert>

              <div className="my-4">
                <h6>What you can do:</h6>
                <ul>
                  <li>Click "Try Again" to reload the component</li>
                  <li>Refresh the page to start fresh</li>
                  <li>If the problem persists, contact support</li>
                </ul>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-3">
                  <summary style={{ cursor: 'pointer', color: '#6c757d' }}>
                    <small>View error details (development only)</small>
                  </summary>
                  <pre className="mt-2 p-3 bg-light border rounded" style={{ fontSize: '0.85rem', overflow: 'auto' }}>
                    <strong>Error:</strong> {this.state.error && this.state.error.toString()}
                    {'\n\n'}
                    <strong>Stack trace:</strong>
                    {'\n'}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="mt-4 d-flex gap-2">
                <Button variant="primary" onClick={this.handleReset}>
                  <i className="fas fa-redo me-2"></i>
                  Try Again
                </Button>
                <Button variant="outline-secondary" onClick={() => window.location.reload()}>
                  <i className="fas fa-sync me-2"></i>
                  Refresh Page
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      );
    }

    // When there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
